import React, { useEffect, useRef, useState } from 'react';
import adminApi from '../lib/adminApi';
import config from '../config';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import '../design/tokens.css';
import './FeedbackInbox.css';

interface Feedback {
  id: string;
  userId: string;
  userName: string;
  username: string;
  message: string;
  platform: string;
  createdAt: string;
}

export default function FeedbackInbox({ refreshKey = 0 }: { refreshKey?: number }) {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  const busy = useRef(false);
  const lastCursor = useRef<string | undefined>(undefined);

  const load = async (nextCursor?: string) => {
    if (nextCursor && busy.current) return;
    const generation = ++request.current;
    busy.current = true;
    lastCursor.current = nextCursor;
    setLoading(true);
    setError('');
    try {
      const { data } = await adminApi.get<{ feedback: Feedback[]; nextCursor: string | null }>(`${config.apiBaseUrl}/admin-dashboard/feedback`, {
        params: { limit: 50, ...(nextCursor ? { cursor: nextCursor } : {}) },
      });
      if (generation !== request.current) return;
      setFeedback(previous => nextCursor ? Array.from(new Map([...previous, ...data.feedback].map(item => [item.id, item])).values()) : data.feedback);
      setCursor(data.nextCursor);
    } catch {
      if (generation === request.current) setError('Couldn’t load feedback. Try again.');
    } finally {
      if (generation === request.current) { busy.current = false; setLoading(false); }
    }
  };

  useEffect(() => {
    void load();
    const pendingRequest = request;
    return () => { pendingRequest.current++; busy.current = false; };
    // Each refresh starts a new first page and ignores any stale page response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return <div className="vs-design feedback-inbox">
    <Card>
      <CardHeader className="feedback-inbox-header">
        <div><CardTitle>Feedback</CardTitle><CardDescription>Messages from the app, newest first.</CardDescription></div>
        <Button variant="outline" disabled={loading} onClick={() => void load()}>Refresh feedback</Button>
      </CardHeader>
      <CardContent>
        {error && <div role="alert" className="feedback-inbox-error">{error} <Button variant="outline" disabled={loading} onClick={() => void load(lastCursor.current)}>Try again</Button></div>}
        {loading && <p role="status">Loading feedback…</p>}
        {!loading && !error && feedback.length === 0 && <p className="feedback-inbox-empty">No feedback yet.</p>}
        <ol className="feedback-inbox-list" aria-label="Feedback messages">
          {feedback.map(item => <li key={item.id}>
            <article>
              <div className="feedback-inbox-meta">
                <div><a href={`/admin/dash#users?user=${encodeURIComponent(item.userId)}`}>{item.userName}</a>{item.username && <span> @{item.username}</span>}</div>
                <div><time dateTime={item.createdAt}>{new Date(item.createdAt).toLocaleString()}</time>{item.platform !== 'unknown' && <span> · {item.platform === 'ios' ? 'iOS' : item.platform === 'android' ? 'Android' : 'Web'}</span>}</div>
              </div>
              <p className="feedback-inbox-message">{item.message}</p>
            </article>
          </li>)}
        </ol>
        {cursor && <Button variant="outline" disabled={loading} onClick={() => void load(cursor)}>Load older feedback</Button>}
      </CardContent>
    </Card>
  </div>;
}
